import { LitElement, PropertyValues } from 'lit';
import { state } from 'lit/decorators.js';

import {
  QuantityPerColorOptionDto,
  QuantityPerSizeOptionDto,
  StepEnum,
} from '@/api-sdk';
import { AnnouncementParams } from '@/types';
import { announceQuantityChange } from '@/utils/utils.accessibility';

type Constructor<T> = new (...args: any[]) => T;

/**
 * Mixin that adds functionality for tracking previous totalPrice and announcing quantity changes
 * @param superClass The class to mix into
 * @returns A class with the mixin applied
 */
export const QuantityTableMixin = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class QuantityTableClass extends superClass {
    @state()
    previousTotalPrice?: string | null;

    @state()
    lastEventBaseQueryString?: string | null;

    /**
     * Handles a quantity set event
     * @param event The custom event
     * @param options The options for the component
     */
    handleQuantitySetAnnouncement(
      event: CustomEvent,
      options: QuantityPerColorOptionDto[] | QuantityPerSizeOptionDto[],
    ) {
      const { quantity, baseQueryString } = event.detail;

      const option = options.find(opt => opt.queryString === baseQueryString);

      if (option && quantity > 0) {
        this.lastEventBaseQueryString = baseQueryString;
      }
    }

    /**
     * Checks if totalPrice has changed and announces the change
     * @param changedProperties The changed properties
     * @param totalPrice The current totalPrice
     * @param options The options for the component
     * @param stepType The step type
     * @param announcementOptions Additional options for the announcement
     */
    checkTotalPriceAndAnnounce(
      changedProperties: PropertyValues,
      totalPrice: string | null | undefined,
      options: QuantityPerColorOptionDto[] | QuantityPerSizeOptionDto[],
      stepType: StepEnum,
      announcementOptions: Partial<AnnouncementParams> = {},
    ) {
      if (
        !changedProperties.has('totalPrice') ||
        !this.lastEventBaseQueryString
      ) {
        return;
      }

      const previousPrice = this.previousTotalPrice;
      this.previousTotalPrice = totalPrice;

      if (!totalPrice || totalPrice === previousPrice) {
        return;
      }

      const matchingOption = options.find(
        option => option.queryString === this.lastEventBaseQueryString,
      );

      if (!matchingOption) {
        return;
      }

      const announcementParams: AnnouncementParams = {
        ...announcementOptions,
        quantity: matchingOption.quantity,
        unitPrice: matchingOption.unitPrice,
        totalPrice: matchingOption.totalPrice,
      };

      announceQuantityChange(
        this as unknown as HTMLElement,
        stepType,
        announcementParams,
      );
    }
  }

  return QuantityTableClass;
};
